import { apiClient } from './api';
import { UrlBuilder } from './urls';
import {
  generateFlutterwavePaymentLink,
  generateFlutterwaveSubscriptionLink,
  redirectToPaymentLink,
} from './flutterwave';

export type PaymentGateway = 'polar' | 'flutterwave' | 'stripe';

export interface PaymentConfig {
  stripe_public_key: string;
  flutterwave_public_key: string;
  polar_public_key?: string;
  payment_gateway?: PaymentGateway;
  credit_point_price: number | string;
  available_subscriptions?: Array<{
    id: number;
    subscription_name: string;
    subscription_type: string;
    subscription_duration: number;
    subscription_price: number;
  }>;
}

export interface CreditCheckoutParams {
  user_id: number;
  amount: number;
  credit_points: number;
  redirect_url: string;
}

export interface SubscriptionCheckoutParams {
  user_id: number;
  subscription_id: number;
  redirect_url: string;
}

export interface CheckoutResponse {
  payment_link: string | null;
  checkout_id?: string;
  tx_ref?: string;
  status: string;
  message: string;
}

export async function fetchPaymentConfig(token: string): Promise<PaymentConfig> {
  const response = await apiClient.getWithToken<PaymentConfig>(
    UrlBuilder.payments.config(),
    token
  );

  if (!response.success) {
    throw new Error(response.error || 'Failed to fetch payment configuration');
  }

  return response.data as PaymentConfig;
}

export function resolvePaymentGateway(config: PaymentConfig): PaymentGateway {
  if (config.payment_gateway) {
    return config.payment_gateway;
  }
  if (config.polar_public_key === 'configured') {
    return 'polar';
  }
  if (config.flutterwave_public_key === 'configured') {
    return 'flutterwave';
  }
  return 'stripe';
}

export function paymentProviderLabel(gateway: PaymentGateway): string {
  switch (gateway) {
    case 'polar':
      return 'Polar';
    case 'flutterwave':
      return 'Flutterwave';
    default:
      return 'Stripe';
  }
}

export async function generateCreditCheckout(
  token: string,
  params: CreditCheckoutParams,
  gateway: PaymentGateway
): Promise<CheckoutResponse> {
  if (gateway === 'polar') {
    const response = await apiClient.postWithToken<CheckoutResponse>(
      UrlBuilder.payments.polarCreditsGenerateLink(),
      token,
      params
    );

    if (!response.success) {
      throw new Error(response.error || 'Failed to generate Polar checkout');
    }

    return response.data as CheckoutResponse;
  }

  const flutterwaveResponse = await generateFlutterwavePaymentLink(token, params);
  return {
    payment_link: flutterwaveResponse.payment_link,
    tx_ref: flutterwaveResponse.tx_ref,
    status: flutterwaveResponse.status,
    message: flutterwaveResponse.message,
  };
}

export async function generateSubscriptionCheckout(
  token: string,
  params: SubscriptionCheckoutParams,
  gateway: PaymentGateway
): Promise<CheckoutResponse> {
  if (gateway === 'polar') {
    const response = await apiClient.postWithToken<CheckoutResponse>(
      UrlBuilder.payments.polarSubscriptionGenerateLink(),
      token,
      params
    );

    if (!response.success) {
      throw new Error(response.error || 'Failed to generate Polar subscription checkout');
    }

    return response.data as CheckoutResponse;
  }

  const flutterwaveResponse = await generateFlutterwaveSubscriptionLink(token, params);
  return {
    payment_link: flutterwaveResponse.payment_link,
    tx_ref: flutterwaveResponse.tx_ref,
    status: flutterwaveResponse.status,
    message: flutterwaveResponse.message,
  };
}

export { redirectToPaymentLink };
